import { useState } from 'react';
import { useFormatting, useTranslation } from '../../../i18n/i18n';
import { Res, useApi } from '../../../state/api';
import { ApiStatisticsChartPoint, ApiStatisticsFilters, ApiStatisticsProductTypeOption } from '../../../types';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { ButtonLink } from '../../Form/Button';
import {
	Icon,
	IconShape,
	coins,
	download,
	eco_tree,
	pdf_file,
	shopping_cart,
	stock_package,
	trash,
	truck,
} from '../../Icon/Icon';
import { Loader } from '../../Loader/Loader';
import { ProductTypeId, clampDate, getPresetForDateRange, useStatisticsSectionFilters } from './statistics-shared';

type ChartMode = 'accumulated' | 'monthly';
type SectionScope = 'total' | 'period';

type StatisticsChartSectionProps = {
	filters: ApiStatisticsFilters;
	includeVat: boolean;
};

type StatisticsScopedSectionProps = {
	filters: ApiStatisticsFilters;
	includeVat: boolean;
};

type StatisticsSummarySectionProps = StatisticsScopedSectionProps & {
	hasSpecials: boolean;
};

const LINE_CHART_WIDTH = 640;
const LINE_CHART_HEIGHT = 260;
const LINE_CHART_PADDING = {
	top: 18,
	right: 16,
	bottom: 26,
	left: 18,
};

type ChartDot = {
	label: string;
	value: number;
	x: number;
	y: number;
};

type StatisticsIconCard = {
	icon: IconShape;
	label: string;
	value: string;
};

type AxisLabel = {
	key: string;
	value: number;
	offsetPercent: number;
};

function getCategoryIcon(label: string): IconShape {
	const normalizedLabel = label.toLowerCase();

	if (normalizedLabel.includes('avfall')) {
		return trash;
	}

	if (
		normalizedLabel.includes('papper') ||
		normalizedLabel.includes('kontor') ||
		normalizedLabel.includes('kopier')
	) {
		return pdf_file;
	}

	if (normalizedLabel.includes('hygien') || normalizedLabel.includes('miljö')) {
		return eco_tree;
	}

	if (normalizedLabel.includes('kaffe') || normalizedLabel.includes('godis')) {
		return stock_package;
	}

	return stock_package;
}

function buildChartDots(points: ApiStatisticsChartPoint[], values: number[]): ChartDot[] {
	const chartMaximum = Math.max(...values, 1);
	const lineChartInnerWidth = LINE_CHART_WIDTH - LINE_CHART_PADDING.left - LINE_CHART_PADDING.right;
	const lineChartInnerHeight = LINE_CHART_HEIGHT - LINE_CHART_PADDING.top - LINE_CHART_PADDING.bottom;

	return values.map((value, index) => {
		const x =
			values.length === 1
				? LINE_CHART_PADDING.left + lineChartInnerWidth / 2
				: LINE_CHART_PADDING.left + (index / (values.length - 1)) * lineChartInnerWidth;
		const y = LINE_CHART_PADDING.top + (1 - value / chartMaximum) * lineChartInnerHeight;

		return {
			label: points[index]?.label ?? '',
			value,
			x,
			y,
		};
	});
}

function getChartY(value: number, maximum: number): number {
	const chartMaximum = Math.max(maximum, 1);
	const lineChartInnerHeight = LINE_CHART_HEIGHT - LINE_CHART_PADDING.top - LINE_CHART_PADDING.bottom;

	return LINE_CHART_PADDING.top + (1 - value / chartMaximum) * lineChartInnerHeight;
}

function buildAxisLabels(maximum: number): AxisLabel[] {
	const topValue = maximum;
	const middleValue = maximum / 2;
	const baseValue = 0;

	return [
		{ key: 'top', value: topValue, offsetPercent: (getChartY(topValue, maximum) / LINE_CHART_HEIGHT) * 100 },
		{
			key: 'mid',
			value: middleValue,
			offsetPercent: (getChartY(middleValue, maximum) / LINE_CHART_HEIGHT) * 100,
		},
		{ key: 'base', value: baseValue, offsetPercent: (getChartY(baseValue, maximum) / LINE_CHART_HEIGHT) * 100 },
	];
}

function getLineTooltipClassName(point: ChartDot): string {
	const classes = ['statistics-chart-tooltip'];

	if (point.y <= LINE_CHART_PADDING.top + 24) {
		classes.push('below');
	}

	if (point.x >= LINE_CHART_WIDTH - LINE_CHART_PADDING.right - 56) {
		classes.push('align-right');
	} else if (point.x <= LINE_CHART_PADDING.left + 56) {
		classes.push('align-left');
	}

	return classes.join(' ');
}

function renderSectionError(className: string) {
	return (
		<section className={className}>
			<ErrorMessage />
		</section>
	);
}

function renderSectionLoader(className: string) {
	return (
		<section className={className}>
			<Loader />
		</section>
	);
}

function renderChartSectionLoader() {
	return (
		<section className="statistics-chart-card statistics-chart-card-loading" aria-busy="true" aria-live="polite">
			<div className="statistics-card-heading">
				<div className="statistics-skeleton-block statistics-skeleton-title" />
				<div className="statistics-card-heading-actions">
					<div className="statistics-skeleton-block statistics-skeleton-value" />
				</div>
			</div>
			<div className="statistics-chart statistics-chart-loading" aria-hidden="true">
				<div className="statistics-line-chart statistics-line-chart-skeleton">
					<div className="statistics-line-chart-graphic statistics-chart-skeleton">
						<div className="statistics-chart-skeleton-guides">
							<div className="statistics-chart-skeleton-guide" />
							<div className="statistics-chart-skeleton-guide" />
							<div className="statistics-chart-skeleton-guide baseline" />
						</div>
						<Loader />
					</div>
					<div className="statistics-line-labels statistics-line-labels-skeleton">
						<div className="statistics-skeleton-block statistics-skeleton-axis-label" />
						<div className="statistics-skeleton-block statistics-skeleton-axis-label" />
						<div className="statistics-skeleton-block statistics-skeleton-axis-label" />
						<div className="statistics-skeleton-block statistics-skeleton-axis-label" />
						<div className="statistics-skeleton-block statistics-skeleton-axis-label" />
						<div className="statistics-skeleton-block statistics-skeleton-axis-label" />
					</div>
				</div>
			</div>
			<div className="statistics-filter-row statistics-filter-row-loading" aria-hidden="true">
				<div className="statistics-skeleton-block statistics-skeleton-pill-group" />
				<div className="statistics-skeleton-block statistics-skeleton-date-range" />
				<div className="statistics-filter-spacer" />
				<div className="statistics-skeleton-block statistics-skeleton-select" />
			</div>
			<div
				className="statistics-filter-row statistics-filter-row-loading statistics-filter-row-loading-secondary"
				aria-hidden="true"
			>
				<div className="statistics-skeleton-block statistics-skeleton-mode-toggle" />
			</div>
		</section>
	);
}

export const StatisticsChartSection = ({ filters, includeVat }: StatisticsChartSectionProps): JSX.Element => {
	const t = useTranslation().statisticsPage;
	const filteringT = useTranslation().filtering;
	const routes = useTranslation().routes;
	const { asLocaleNumber, asPrice } = useFormatting();
	const [chartMode, setChartMode] = useState<ChartMode>('accumulated');
	const [activeChartPointIndex, setActiveChartPointIndex] = useState<number | null>(null);
	const {
		isReady,
		selectedPeriod,
		selectedPeriodLabel,
		periodStartDate,
		periodEndDate,
		selectedProductType,
		requestParams,
		setSelectedPeriod,
		setPeriodStartDate,
		setPeriodEndDate,
		setSelectedProductType,
	} = useStatisticsSectionFilters(filters, includeVat, filteringT.to);
	const productTypeOptions = filters.productTypes.map((productType) =>
		productType.id === 'all' ? { ...productType, label: t.allProductTypes } : productType
	);
	const { data: chartData, error: chartError } = useApi(isReady ? Res.statisticsChart : null, requestParams);

	if (!isReady) {
		return renderChartSectionLoader();
	}

	if (chartError && !chartData) {
		return renderSectionError('statistics-chart-card');
	}

	if (!chartData) {
		return renderChartSectionLoader();
	}

	const displayedChartValues = chartData.points.map((point) =>
		chartMode === 'accumulated' ? point.cumulativeValue : point.monthValue
	);
	const chartMaximum = Math.max(...displayedChartValues, 1);
	const chartDots = buildChartDots(chartData.points, displayedChartValues);
	const axisLabels = buildAxisLabels(chartMaximum);
	const linePoints = chartDots.map((point) => `${point.x},${point.y}`).join(' ');
	const activeChartDot = activeChartPointIndex === null ? null : chartDots[activeChartPointIndex];
	const chartColumnStyle = {
		gridTemplateColumns: `repeat(${Math.max(chartData.points.length, 1)}, minmax(0, 1fr))`,
	};
	return (
		<div className="statistics-chart-wrapper">
			<section className="statistics-chart-card">
				<div className="statistics-card-heading">
					<div>
						<p className="statistics-eyebrow">{t.chartTitle}</p>
					</div>
					<div className="statistics-card-heading-actions">
						<strong>{asPrice(chartData.selectedPeriodVolume, 0)}</strong>
						<span className="statistics-card-heading-meta">{selectedPeriodLabel}</span>
					</div>
				</div>

				<div className={`statistics-chart ${chartMode}`}>
					{chartMode === 'monthly' ? (
						<div className="statistics-chart-plot">
							<div className="statistics-y-axis" aria-hidden="true">
								{axisLabels.map((label) => (
									<span key={label.key} style={{ top: `${label.offsetPercent}%` }}>
										{asLocaleNumber(Math.round(label.value))}
									</span>
								))}
							</div>
							<div className="statistics-chart-plot-content">
								<div className="statistics-bars" aria-label={t.perMonth} style={chartColumnStyle}>
									{chartData.points.map((point, index) => (
										<div
											key={point.key}
											className="statistics-bar-column"
											onMouseEnter={() => setActiveChartPointIndex(index)}
											onMouseLeave={() => setActiveChartPointIndex(null)}
										>
											<div className="statistics-bar-track">
												{activeChartPointIndex === index ? (
													<div
														className="statistics-chart-tooltip statistics-chart-tooltip-inline"
														role="status"
														style={{
															bottom: `max(calc(${(point.monthValue / chartMaximum) * 100}% - 2.75rem), 0.5rem)`,
														}}
													>
														<span className="statistics-bar-label">{point.label}</span>
														<strong>{asPrice(point.monthValue, 0)}</strong>
													</div>
												) : null}
												<div
													className="statistics-bar"
													style={{ height: `${(point.monthValue / chartMaximum) * 100}%` }}
													tabIndex={0}
													aria-label={`${point.label}: ${asPrice(point.monthValue, 0)}`}
													onFocus={() => setActiveChartPointIndex(index)}
													onBlur={() => setActiveChartPointIndex(null)}
												/>
											</div>
											<span className="statistics-bar-label">{point.label}</span>
										</div>
									))}
								</div>
							</div>
						</div>
					) : (
						<div className="statistics-line-chart" aria-label={t.accumulated}>
							<div className="statistics-chart-plot">
								<div className="statistics-y-axis" aria-hidden="true">
									{axisLabels.map((label) => (
										<span key={label.key} style={{ top: `${label.offsetPercent}%` }}>
											{asLocaleNumber(Math.round(label.value))}
										</span>
									))}
								</div>
								<div className="statistics-chart-plot-content">
									<div className="statistics-line-chart-scroll">
										<div className="statistics-line-chart-graphic">
											{activeChartDot ? (
												<div
													className={getLineTooltipClassName(activeChartDot)}
													role="status"
													style={{
														left: `${(activeChartDot.x / LINE_CHART_WIDTH) * 100}%`,
														top: `${(activeChartDot.y / LINE_CHART_HEIGHT) * 100}%`,
													}}
												>
													<span>{activeChartDot.label}</span>
													<strong>{asPrice(activeChartDot.value, 0)}</strong>
												</div>
											) : null}
											<svg viewBox={`0 0 ${LINE_CHART_WIDTH} ${LINE_CHART_HEIGHT}`} role="img">
												<line
													className="statistics-line-guide"
													x1={LINE_CHART_PADDING.left}
													y1={LINE_CHART_PADDING.top}
													x2={LINE_CHART_WIDTH - LINE_CHART_PADDING.right}
													y2={LINE_CHART_PADDING.top}
												/>
												<line
													className="statistics-line-guide"
													x1={LINE_CHART_PADDING.left}
													y1={
														LINE_CHART_PADDING.top +
														(LINE_CHART_HEIGHT -
															LINE_CHART_PADDING.top -
															LINE_CHART_PADDING.bottom) /
															2
													}
													x2={LINE_CHART_WIDTH - LINE_CHART_PADDING.right}
													y2={
														LINE_CHART_PADDING.top +
														(LINE_CHART_HEIGHT -
															LINE_CHART_PADDING.top -
															LINE_CHART_PADDING.bottom) /
															2
													}
												/>
												<line
													className="statistics-line-guide baseline"
													x1={LINE_CHART_PADDING.left}
													y1={LINE_CHART_HEIGHT - LINE_CHART_PADDING.bottom}
													x2={LINE_CHART_WIDTH - LINE_CHART_PADDING.right}
													y2={LINE_CHART_HEIGHT - LINE_CHART_PADDING.bottom}
												/>
												<polyline className="statistics-line-path" points={linePoints} />
												{chartDots.map((point, index) => (
													<circle
														key={chartData.points[index].key}
														className="statistics-line-dot"
														cx={point.x}
														cy={point.y}
														r="8"
														tabIndex={0}
														aria-label={`${point.label}: ${asPrice(point.value, 0)}`}
														onMouseEnter={() => setActiveChartPointIndex(index)}
														onMouseLeave={() => setActiveChartPointIndex(null)}
														onFocus={() => setActiveChartPointIndex(index)}
														onBlur={() => setActiveChartPointIndex(null)}
													>
														<title>
															{point.label}: {asPrice(point.value, 0)}
														</title>
													</circle>
												))}
											</svg>
										</div>
										<div className="statistics-line-labels" style={chartColumnStyle}>
											{chartData.points.map((point) => (
												<span key={point.key}>{point.label}</span>
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>

				<div className="statistics-chart-controls">
					<div className="statistics-filter-row statistics-filter-row-primary">
						<div className="statistics-pill-group" role="group" aria-label={t.pickPeriod}>
							{filters.presets.map((preset) => (
								<button
									key={preset.id}
									type="button"
									className={preset.id === selectedPeriod ? 'active' : ''}
									onClick={() => {
										setSelectedPeriod(preset.id);
										setPeriodStartDate(preset.startDate);
										setPeriodEndDate(preset.endDate);
										setActiveChartPointIndex(null);
									}}
								>
									{preset.label}
								</button>
							))}
						</div>
						<div className="statistics-date-range" role="group" aria-label={t.pickPeriod}>
							<input
								type="date"
								aria-label={`${t.pickPeriod} start`}
								min={filters.startDate}
								max={periodEndDate}
								value={periodStartDate}
								onChange={(event) => {
									const nextStartDate = clampDate(
										event.target.value,
										filters.startDate,
										filters.endDate
									);
									const nextEndDate = nextStartDate > periodEndDate ? nextStartDate : periodEndDate;

									setPeriodStartDate(nextStartDate);
									setPeriodEndDate(nextEndDate);
									setSelectedPeriod(
										getPresetForDateRange(filters.presets, nextStartDate, nextEndDate)?.id ?? null
									);
									setActiveChartPointIndex(null);
								}}
							/>
							<span>{filteringT.to}</span>
							<input
								type="date"
								aria-label={`${t.pickPeriod} end`}
								min={periodStartDate}
								max={filters.endDate}
								value={periodEndDate}
								onChange={(event) => {
									const nextEndDate = clampDate(
										event.target.value,
										filters.startDate,
										filters.endDate
									);
									const nextStartDate = nextEndDate < periodStartDate ? nextEndDate : periodStartDate;

									setPeriodStartDate(nextStartDate);
									setPeriodEndDate(nextEndDate);
									setSelectedPeriod(
										getPresetForDateRange(filters.presets, nextStartDate, nextEndDate)?.id ?? null
									);
									setActiveChartPointIndex(null);
								}}
							/>
						</div>
						<div className="statistics-filter-spacer" />
						<label className="statistics-select" aria-label={t.allProductTypes}>
							<select
								aria-label={t.allProductTypes}
								value={selectedProductType}
								onChange={(event) => {
									setSelectedProductType(event.target.value as ProductTypeId);
									setActiveChartPointIndex(null);
								}}
							>
								{productTypeOptions.map((option: ApiStatisticsProductTypeOption) => (
									<option key={option.id} value={option.id}>
										{option.label}
									</option>
								))}
							</select>
						</label>
					</div>
					<div className="statistics-filter-row statistics-filter-row-secondary">
						<div className="statistics-pill-group" role="group" aria-label={t.chartMode}>
							<button
								type="button"
								className={chartMode === 'accumulated' ? 'active' : ''}
								onClick={() => setChartMode('accumulated')}
							>
								{t.accumulated}
							</button>
							<button
								type="button"
								className={chartMode === 'monthly' ? 'active' : ''}
								onClick={() => setChartMode('monthly')}
							>
								{t.perMonth}
							</button>
						</div>
					</div>
				</div>
			</section>

			<div className="statistics-download-card">
				<div className="statistics-download-icon">
					<Icon shape={download} size={24} />
				</div>
				<div className="statistics-download-content">
					<p>{t.downloadNotice}</p>
					<ButtonLink className="small" linkTo={routes.exportStatistics}>
						{t.downloadButton}
					</ButtonLink>
				</div>
			</div>
		</div>
	);
};

export const StatisticsCategoriesSection = ({ filters, includeVat }: StatisticsScopedSectionProps): JSX.Element => {
	const t = useTranslation().statisticsPage;
	const filteringT = useTranslation().filtering;
	const { asPrice } = useFormatting();
	const [categoryScope, setCategoryScope] = useState<SectionScope>('total');
	const { isReady, requestParams, selectedPeriodLabel } = useStatisticsSectionFilters(
		filters,
		includeVat,
		filteringT.to
	);

	const { data, error } = useApi(isReady ? Res.statisticsCategories : null, requestParams);

	if (!isReady) {
		return renderSectionLoader('statistics-section');
	}

	if (error && !data) {
		return renderSectionError('statistics-section');
	}

	if (!data) {
		return renderSectionLoader('statistics-section');
	}

	const items = categoryScope === 'total' ? data.total : data.period;

	return (
		<section className="statistics-section">
			<div className="statistics-section-heading">
				<h2>{t.categoriesTitle}</h2>
				<div className="statistics-mini-pills" role="group" aria-label={t.categoriesTitle}>
					<button
						type="button"
						className={categoryScope === 'total' ? 'active' : ''}
						onClick={() => setCategoryScope('total')}
					>
						{t.total}
					</button>
					<button
						type="button"
						className={categoryScope === 'period' ? 'active' : ''}
						onClick={() => setCategoryScope('period')}
					>
						{selectedPeriodLabel}
					</button>
				</div>
			</div>

			<div className="statistics-categories">
				{items.map((category) => (
					<div
						key={category.label}
						className={`statistics-category-bar ${category.theme}`}
						style={{ width: `${(category.amount / Math.max(items[0]?.amount ?? 1, 1)) * 100}%` }}
					>
						<div className="statistics-category-label">
							<span className="statistics-category-icon" aria-hidden="true">
								<Icon shape={getCategoryIcon(category.label)} size={14} />
							</span>
							<span>{category.label}</span>
						</div>
						<strong>{asPrice(category.amount, 0)}</strong>
					</div>
				))}
			</div>
		</section>
	);
};

export const StatisticsEcoSection = ({ filters, includeVat }: StatisticsScopedSectionProps): JSX.Element => {
	const t = useTranslation().statisticsPage;
	const filteringT = useTranslation().filtering;
	const [ecoScope, setEcoScope] = useState<SectionScope>('total');
	const { isReady, requestParams, selectedPeriodLabel } = useStatisticsSectionFilters(
		filters,
		includeVat,
		filteringT.to
	);

	const { data, error } = useApi(isReady ? Res.statisticsEco : null, requestParams);

	if (!isReady) {
		return renderSectionLoader('statistics-section');
	}

	if (error && !data) {
		return renderSectionError('statistics-section');
	}

	if (!data) {
		return renderSectionLoader('statistics-section');
	}

	const metrics = ecoScope === 'total' ? data.total : data.period;

	return (
		<section className="statistics-section">
			<div className="statistics-section-heading">
				<h2>{t.ecoTitle}</h2>
				<div className="statistics-mini-pills" role="group" aria-label={t.ecoTitle}>
					<button
						type="button"
						className={ecoScope === 'total' ? 'active' : ''}
						onClick={() => setEcoScope('total')}
					>
						{t.total}
					</button>
					<button
						type="button"
						className={ecoScope === 'period' ? 'active' : ''}
						onClick={() => setEcoScope('period')}
					>
						{selectedPeriodLabel}
					</button>
				</div>
			</div>

			<div className="statistics-eco-split">
				<div className="statistics-eco-split-panel strong" style={{ width: metrics.ecoShare + '%' }}>
					<strong>{metrics.ecoShare}%</strong>
					<span>{t.withEcoLabel}</span>
				</div>
				<div className="statistics-eco-split-panel light" style={{ width: metrics.nonEcoShare + '%' }}>
					<strong>{metrics.nonEcoShare}%</strong>
					<span>{t.withoutEcoLabel}</span>
				</div>
			</div>

			<div className="statistics-insight-grid">
				<div className="statistics-insight-card">
					<h3>{t.greatJobTitle}</h3>
					<p>{t.greatJobText}</p>
					<div className="statistics-progress-block">
						<div className="statistics-progress-label">{t.you}</div>
						<div className="statistics-progress-fill" style={{ width: `${metrics.ecoShare}%` }} />
					</div>
					<div className="statistics-progress-block muted">
						<div className="statistics-progress-label">{t.averageLabel}</div>
						<div className="statistics-progress-fill muted" style={{ width: `${metrics.ecoAverage}%` }} />
					</div>
				</div>

				<div className="statistics-insight-card">
					<div className="statistics-insight-content">
						<h3>{t.shopSmarterTitle}</h3>
						<p>{t.shopSmarterText}</p>
					</div>
					<div className="statistics-insight-icon">
						<Icon shape={eco_tree} size={88} />
					</div>
				</div>
			</div>
		</section>
	);
};

export const StatisticsConsumptionSection = ({ filters, includeVat }: StatisticsScopedSectionProps): JSX.Element => {
	const t = useTranslation().statisticsPage;
	const filteringT = useTranslation().filtering;
	const { asLocaleNumber } = useFormatting();
	const { isReady, requestParams } = useStatisticsSectionFilters(filters, includeVat, filteringT.to);

	const { data, error } = useApi(isReady ? Res.statisticsConsumption : null, requestParams);

	if (!isReady) {
		return renderSectionLoader('statistics-section');
	}

	if (error && !data) {
		return renderSectionError('statistics-section');
	}

	if (!data) {
		return renderSectionLoader('statistics-section');
	}

	const cards: StatisticsIconCard[] = [
		{ icon: stock_package, label: t.coffeeCups, value: asLocaleNumber(data.coffeeCups) },
		{ icon: coins, label: t.cookiesAndCandy, value: `${asLocaleNumber(data.cookiesAndCandy)} g` },
		{ icon: pdf_file, label: t.a4Sheets, value: asLocaleNumber(data.a4Sheets) },
		{ icon: trash, label: t.trashBags, value: asLocaleNumber(data.trashBags) },
	];

	return (
		<section className="statistics-section">
			<div className="statistics-section-heading stacked">
				<div>
					<h2>{t.consumptionTitle}</h2>
					<p>{t.consumptionSubtitle}</p>
				</div>
			</div>

			<div className="statistics-consumption-grid">
				{cards.map((card) => (
					<div key={card.label} className="statistics-consumption-card">
						<div className="statistics-consumption-icon" aria-hidden="true">
							<Icon shape={card.icon} size={18} />
						</div>
						<strong>{card.value}</strong>
						<span>{card.label}</span>
					</div>
				))}
			</div>
		</section>
	);
};

export const StatisticsSummarySection = ({
	filters,
	includeVat,
	hasSpecials,
}: StatisticsSummarySectionProps): JSX.Element => {
	const t = useTranslation().statisticsPage;
	const filteringT = useTranslation().filtering;
	const { asLocaleNumber, asPrice } = useFormatting();
	const { isReady, requestParams } = useStatisticsSectionFilters(filters, includeVat, filteringT.to);

	const { data, error } = useApi(isReady ? Res.statisticsSummary : null, requestParams);

	if (!isReady) {
		return renderSectionLoader('statistics-summary-grid');
	}

	if (error && !data) {
		return renderSectionError('statistics-summary-grid');
	}

	if (!data) {
		return renderSectionLoader('statistics-summary-grid');
	}

	return (
		<section className="statistics-summary-grid">
			<div className="statistics-summary-card">
				<div>
					<p>{t.averageOrder}</p>
					<strong>{asPrice(data.averageOrderValue, 0)}</strong>
				</div>
				<div className="statistics-summary-icon" aria-hidden="true">
					<Icon shape={shopping_cart} size={88} />
				</div>
			</div>
			<div className="statistics-summary-card">
				<div>
					<p>{t.totalOrders}</p>
					<strong>{asLocaleNumber(data.orderCount)}</strong>
				</div>
				<div className="statistics-summary-icon" aria-hidden="true">
					<Icon shape={truck} size={88} />
				</div>
			</div>
			{hasSpecials ? (
				<div className="statistics-summary-card accent">
					<div>
						<p>{t.savedWithSpecialPrices}</p>
						<strong>{asPrice(data.specialSavings, 0)}</strong>
					</div>
					<div className="statistics-summary-icon" aria-hidden="true">
						<Icon shape={coins} size={88} />
					</div>
				</div>
			) : null}
		</section>
	);
};
